import * as React from 'react';
import { createContext, useContext } from 'react';
import { graphql } from 'react-apollo';

import { compose } from 'recompose';

import { get } from 'js/lib/user';

import { withProductFeatures } from 'bundles/admin-dashboard/features';
import type { ThirdPartyOrganization } from 'bundles/admin-dashboard/types';
import type {
  AdminAutoEnrollmentQuery as AutoEnrollmentQuery,
  AdminAutoEnrollmentQueryVariables as AutoEnrollmentQueryVariables,
} from 'bundles/enterprise-admin-program-catalog/__generated__/AdminAutoEnrollmentQuery';
import { autoEnrollmentQuery } from 'bundles/enterprise-admin-program-catalog/queries';
import type { Program } from 'bundles/enterprise-admin-program-home/types';
import type ProgramCurriculumsV1 from 'bundles/naptimejs/resources/programCurriculums.v1';
import { IdTypes } from 'bundles/product-features';
import { getProgramCareerAcademyVariantQueryGraphQl } from 'bundles/unified-career-academy/queries/enterpriseQueries';
import { isCareerAcademyFreemiumEnabled, isCareerAcademyV2Enabled } from 'bundles/unified-career-academy/utils/helpers';
import type { PropCareerAcademyVariant } from 'bundles/unified-career-academy/utils/helpers';

export const ProgramAdminCatalogPermissions = {
  CAN_ADD_COLLECTION: 'CAN_ADD_COLLECTION',
  CAN_ADD_PRODUCTS_TO_COLLECTION: 'CAN_ADD_PRODUCTS_TO_COLLECTION',
  CAN_DELETE_COLLECTION: 'CAN_DELETE_COLLECTION',
  CAN_EDIT_COLLECTION_NAME_AND_DESCRIPTION: 'CAN_EDIT_COLLECTION_NAME_AND_DESCRIPTION',
  CAN_REORDER_PRODUCTS_IN_COLLECTION: 'CAN_REORDER_PRODUCTS_IN_COLLECTION',
  CAN_EDIT_COURSE_SESSIONS: 'CAN_EDIT_COURSE_SESSIONS',
  CAN_SELECT_ROLES: 'CAN_SELECT_ROLES',
} as const;

export type ProgramAdminCatalogPermissionsType =
  (typeof ProgramAdminCatalogPermissions)[keyof typeof ProgramAdminCatalogPermissions];

type ProgramAdminTypeAndPermissionsContextProps = {
  userId?: number;
  programId: string;
  orgId: string;
  isC4C: boolean;
  isC4CB: boolean;
  hasAllRequiredPermissions: (permissions: ProgramAdminCatalogPermissionsType[]) => boolean;
};

export const ProgramAdminTypeAndPermissionsContext = createContext<ProgramAdminTypeAndPermissionsContextProps>({
  userId: 0,
  programId: '',
  orgId: '',
  isC4C: false,
  isC4CB: false,
  hasAllRequiredPermissions: () => false,
});

export const useProgramAdminTypeAndPermissionsContext = () => {
  return useContext(ProgramAdminTypeAndPermissionsContext);
};

type PropsFromAutoEnrollmentQuery = {
  hasSingleAutoEnrolledCourse?: boolean;
};

type PropsFromWithProductFeatures = {
  isCareerAcademyLimitedCatalog: boolean;
};

type PropsFromCaller = {
  curriculum?: ProgramCurriculumsV1;
  program: { id: string };
  isFreemium?: boolean;
  contractTag?: string;
  thirdPartyOrganization?: ThirdPartyOrganization;
};

type Props = PropsFromCaller & PropsFromAutoEnrollmentQuery & PropsFromWithProductFeatures & PropCareerAcademyVariant;

export const ProgramAdminTypeAndPermissionsContextProvider: React.LegacyFunctionComponentWithChildren<Props> = ({
  contractTag,
  hasSingleAutoEnrolledCourse,
  isFreemium,
  program,
  thirdPartyOrganization,
  isCareerAcademyLimitedCatalog,
  careerAcademyVariant,
  children,
}) => {
  const isC4CB = Boolean(hasSingleAutoEnrolledCourse && isFreemium);
  const isC4C = thirdPartyOrganization?.organizationType === 'CAMPUS';
  const userId = get().id;

  const isCareerAcademyFreemium = isCareerAcademyFreemiumEnabled(careerAcademyVariant);
  const isCareerAcademyProgram =
    isCareerAcademyV2Enabled(careerAcademyVariant) || isCareerAcademyFreemium || isCareerAcademyLimitedCatalog;

  const hasRequiredPermission = (permission: ProgramAdminCatalogPermissionsType) => {
    switch (permission) {
      case ProgramAdminCatalogPermissions.CAN_ADD_COLLECTION:
        return !isCareerAcademyLimitedCatalog;
      case ProgramAdminCatalogPermissions.CAN_DELETE_COLLECTION:
        return !isC4CB && !isCareerAcademyLimitedCatalog && !isCareerAcademyFreemium;
      case ProgramAdminCatalogPermissions.CAN_EDIT_COLLECTION_NAME_AND_DESCRIPTION:
        return !isC4CB && !isCareerAcademyLimitedCatalog && !isCareerAcademyFreemium;
      case ProgramAdminCatalogPermissions.CAN_ADD_PRODUCTS_TO_COLLECTION:
        return !isC4CB && !isCareerAcademyLimitedCatalog && !isCareerAcademyFreemium;
      case ProgramAdminCatalogPermissions.CAN_REORDER_PRODUCTS_IN_COLLECTION:
        return !isCareerAcademyLimitedCatalog && !isCareerAcademyFreemium;
      case ProgramAdminCatalogPermissions.CAN_EDIT_COURSE_SESSIONS:
        return true;
      case ProgramAdminCatalogPermissions.CAN_SELECT_ROLES:
        return isCareerAcademyProgram && !isCareerAcademyFreemium;
      default:
        return false;
    }
  };

  const hasAllRequiredPermissions = (permissions: ProgramAdminCatalogPermissionsType[]) => {
    return permissions.every((permission) => hasRequiredPermission(permission));
  };

  return (
    <ProgramAdminTypeAndPermissionsContext.Provider
      value={{
        userId,
        programId: program?.id ?? '',
        orgId: thirdPartyOrganization?.id ?? '',
        isC4C,
        isC4CB,
        hasAllRequiredPermissions,
      }}
    >
      {children}
    </ProgramAdminTypeAndPermissionsContext.Provider>
  );
};

export default compose<Props, React.PropsWithChildren<PropsFromCaller>>(
  withProductFeatures<PropsFromWithProductFeatures, PropsFromCaller>(
    ({ program }) => ({
      idType: IdTypes.Program,
      id: program.id,
    }),
    ({ features }) => ({
      isCareerAcademyLimitedCatalog: features.get('enterprise', 'isCareerAcademyLimitedCatalog'),
    })
  ),
  getProgramCareerAcademyVariantQueryGraphQl<{ program: Program }>(),
  graphql<PropsFromCaller, AutoEnrollmentQuery, AutoEnrollmentQueryVariables, PropsFromAutoEnrollmentQuery>(
    autoEnrollmentQuery,
    {
      skip: ({ curriculum, isFreemium, program }) => {
        if (!program?.id || !isFreemium) return true;

        const coreTracks = curriculum?.definition?.coreTracks;
        const electiveTracks = curriculum?.definition?.electiveTracks;

        if ((coreTracks?.length ?? 0) + (electiveTracks?.length ?? 0) !== 1) return true;
        const allProducts = [
          ...(coreTracks?.[0]?.curriculumItems ?? []),
          ...(electiveTracks?.[0]?.curriculumItems ?? []),
        ];

        if (allProducts.length !== 1 && allProducts[0]?.typeName !== 'courseId') return true;
        return false;
      },
      options: ({ program }) => ({
        variables: { programId: program.id },
      }),
      props: ({ data, ownProps }) => {
        const { curriculum } = ownProps;
        const enrollmentSetting = data?.ProgramEnrollmentSettingsV1Resource?.byTargetProgram?.elements[0];
        const hasJoinedProgramTrigger = !!enrollmentSetting?.triggerEventJoinedProgram;
        const firstItem =
          curriculum?.definition?.coreTracks?.[0]?.curriculumItems?.[0] ||
          curriculum?.definition?.electiveTracks?.[0]?.curriculumItems?.[0];
        let curriculumCourseId: string | undefined;
        if (firstItem?.typeName !== 'clipId') {
          curriculumCourseId = firstItem?.definition?.id;
        }
        const hasAutoEnrolledCourseTrigger = enrollmentSetting?.enrollmentTargetCourse?.id === curriculumCourseId;
        return { hasSingleAutoEnrolledCourse: hasAutoEnrolledCourseTrigger && hasJoinedProgramTrigger };
      },
    }
  )
)(ProgramAdminTypeAndPermissionsContextProvider);
